import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Pencil,
	Trash2,
	Plus,
	Search,
	Receipt,
	CreditCard,
	Users,
	CircleCheck,
	Clock,
	TriangleAlert,
	Pause,
	Ban,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import CompanyModal from "../../components/CompanyModal";
import CompanyUsersModal from "../../components/CompanyUsersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const brl = (v) => formatarMoeda(v, moedaPlataforma());

// Cada situação da assinatura vira uma aba, com a cor do estado.
const ABAS = [
	{ value: "todas", rotulo: "Todas", icone: Users },
	{ value: "active", rotulo: "Ativas", icone: CircleCheck },
	{ value: "trial", rotulo: "Em teste", icone: Clock },
	{ value: "past_due", rotulo: "Inadimplentes", icone: TriangleAlert },
	{ value: "paused", rotulo: "Pausadas", icone: Pause },
	{ value: "canceled", rotulo: "Canceladas", icone: Ban },
];

const SITUACAO = {
	active: {
		rotulo: "Ativa",
		classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	},
	trial: {
		rotulo: "Em teste",
		classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
	},
	past_due: {
		rotulo: "Em atraso",
		classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
	},
	paused: {
		rotulo: "Pausada",
		classe: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
	},
	canceled: {
		rotulo: "Cancelada",
		classe: "bg-destructive/10 text-destructive",
	},
	blocked: {
		rotulo: "Bloqueada",
		classe: "bg-destructive/10 text-destructive",
	},
};

const Companies = () => {
	// Puxa a moeda da plataforma; os formatadores acima leem dela.
	useMoedaAtiva();

	const navigate = useNavigate();
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(false);
	const [busca, setBusca] = useState("");
	const [aba, setAba] = useState("todas");
	const [contadores, setContadores] = useState({});
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [usersOpen, setUsersOpen] = useState(false);
	const [empresaUsers, setEmpresaUsers] = useState(null);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/saas/companies", {
				params: { searchParam: busca, status: aba },
			});
			setCompanies(data.companies);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, [busca, aba]);

	// Os contadores das abas vêm dos indicadores, não da lista filtrada.
	const carregarContadores = useCallback(async () => {
		try {
			const { data } = await api.get("/saas/metrics");
			setContadores(data.empresas || {});
		} catch (err) {
			// Sem contador a tela continua utilizável.
		}
	}, []);

	useEffect(() => {
		const debounce = setTimeout(carregar, 400);
		return () => clearTimeout(debounce);
	}, [carregar]);

	useEffect(() => {
		carregarContadores();
	}, [carregarContadores]);

	const fecharModal = () => {
		setModalOpen(false);
		setSelecionada(null);
		carregar();
		carregarContadores();
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/companies/${id}`);
			toast.success("Empresa excluída.");
			carregar();
			carregarContadores();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	const abas = ABAS.map((item) => ({
		...item,
		contador:
			item.value === "todas"
				? contadores.total || 0
				: contadores[item.value] || 0,
	}));

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionada && `Excluir ${selecionada.name}?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={() => excluir(selecionada.id)}
			>
				Todos os dados desta empresa — usuários, conversas e conexões — serão
				removidos. Esta ação não pode ser desfeita.
			</ConfirmationModal>

			<CompanyModal
				open={modalOpen}
				onClose={fecharModal}
				companyId={selecionada?.id}
			/>

			<CompanyUsersModal
				open={usersOpen}
				onClose={() => {
					setUsersOpen(false);
					setEmpresaUsers(null);
				}}
				company={empresaUsers}
			/>

			{/* cabeçalho */}
			<div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
				<h1 className="flex-1 truncate text-lg font-semibold tracking-tight">
					Empresas
				</h1>
				<div className="relative">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder="Nome, e-mail, documento…"
						value={busca}
						onChange={(e) => setBusca(e.target.value)}
						className="h-9 w-full pl-9 sm:w-56"
					/>
				</div>
				<Button
					className="h-9"
					onClick={() => {
						setSelecionada(null);
						setModalOpen(true);
					}}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">Nova empresa</span>
					<span className="sm:hidden">Nova</span>
				</Button>
			</div>

			<PageTabs abas={abas} atual={aba} onChange={setAba} />

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{/* Cartões no mobile: tabela em tela estreita fica ilegível. */}
				<div className="space-y-2 sm:hidden">
					{companies.map((company) => {
						const situacao = SITUACAO[company.status] || SITUACAO.active;
						return (
							<button
								key={company.id}
								type="button"
								onClick={() =>
									navigate(`/companies/${company.id}/subscription`)
								}
								className="w-full rounded-xl border bg-card p-3 text-left"
							>
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate font-medium">{company.name}</p>
										{company.email && (
											<p className="truncate text-xs text-muted-foreground">
												{company.email}
											</p>
										)}
									</div>
									<span
										className={cn(
											"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
											situacao.classe
										)}
									>
										{situacao.rotulo}
									</span>
								</div>
								<div className="mt-2 flex items-center justify-between text-xs">
									<span className="text-muted-foreground">
										{company.plan?.name || "Sem plano"}
									</span>
									<span className="font-medium tabular-nums">
										{brl(company.total)}
									</span>
								</div>
							</button>
						);
					})}
					{!loading && companies.length === 0 && (
						<p className="py-10 text-center text-sm text-muted-foreground">
							Nenhuma empresa nesta situação.
						</p>
					)}
				</div>

				{/* Tabela no desktop */}
				<div className="hidden overflow-x-auto rounded-xl border bg-card sm:block">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Empresa</TableHead>
								<TableHead>Plano</TableHead>
								<TableHead className="text-right">Mensalidade</TableHead>
								<TableHead className="text-center">Vencimento</TableHead>
								<TableHead className="text-center">Situação</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{companies.map((company) => {
								const situacao = SITUACAO[company.status] || SITUACAO.active;
								return (
									<TableRow key={company.id}>
										<TableCell>
											<span className="font-medium">{company.name}</span>
											{company.email && (
												<span className="block text-xs text-muted-foreground">
													{company.email}
												</span>
											)}
											{company.tags?.length > 0 && (
												<span className="mt-1 flex flex-wrap gap-1">
													{company.tags.map((tag) => (
														<span
															key={tag.id}
															className="rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
															style={{ backgroundColor: tag.color }}
														>
															{tag.name}
														</span>
													))}
												</span>
											)}
										</TableCell>
										<TableCell>
											{company.plan?.name || (
												<span className="text-muted-foreground">—</span>
											)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{brl(company.total)}
										</TableCell>
										<TableCell className="text-center tabular-nums">
											{company.dueDate
												? format(parseISO(company.dueDate), "dd/MM/yyyy")
												: "—"}
										</TableCell>
										<TableCell className="text-center">
											<span
												className={cn(
													"inline-block rounded-full px-2 py-0.5 text-xs font-medium",
													situacao.classe
												)}
											>
												{situacao.rotulo}
											</span>
										</TableCell>
										<TableCell>
											<div className="flex items-center justify-center gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Gerenciar assinatura"
													onClick={() =>
														navigate(
															`/companies/${company.id}/subscription`
														)
													}
												>
													<CreditCard className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Ver faturas"
													onClick={() =>
														navigate(`/invoices?companyId=${company.id}`)
													}
												>
													<Receipt className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Usuários e senhas"
													onClick={() => {
														setEmpresaUsers(company);
														setUsersOpen(true);
													}}
												>
													<Users className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Editar empresa"
													onClick={() => {
														setSelecionada(company);
														setModalOpen(true);
													}}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Excluir empresa"
													onClick={() => {
														setSelecionada(company);
														setConfirmOpen(true);
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
							{loading && <TableRowSkeleton columns={6} />}
							{!loading && companies.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={6}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhuma empresa nesta situação.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default Companies;
